import Vue from 'vue'

/*
 * En GET, les paramètres de requête passent par `data`, pas par `params` : auth.fetch recompose
 * sa configuration axios à la main et ne recopie pas `params` (voir ExportRepository).
 */

/**
 * Le module d'inscription — distinct de PaymentOnboardingRepository, qui sert l'ancien
 * enrôlement SEPA et n'est pas touché.
 */
class RegistrationRepository {

  // GET /api/onboarding/registrations
  list({ organizationId, pending, status, search, offset, limit } = {}) {
    const params = {}
    if (organizationId) params.organizationId = organizationId
    if (pending) params.pending = 1
    if (status && status.length) params.status = status
    if (search) params.search = search
    if (offset !== undefined) params.offset = offset
    if (limit !== undefined) params.limit = limit

    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: '/api/onboarding/registrations', method: 'get', data: params })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // GET /api/onboarding/registrations/{id}
  get(id) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}`, method: 'get' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations
  create(data) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: '/api/onboarding/registrations', method: 'post', data })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations/import-preview — lit le fichier sans rien créer : chaque ligne valide, déjà connue ou en erreur
  previewImport(organizationId, file, policyId = null) {
    return this.upload('/api/onboarding/registrations/import-preview' + (policyId ? `?policyId=${policyId}` : ''), organizationId, file)
  }

  // POST /api/onboarding/registrations/import — relit le même fichier et crée les dossiers des lignes valides
  importFile(organizationId, file, policyId = null) {
    return this.upload('/api/onboarding/registrations/import' + (policyId ? `?policyId=${policyId}` : ''), organizationId, file)
  }

  // GET /api/onboarding/registrations/import-template — le modèle de fichier
  importTemplate() {
    return Vue.auth.download({ url: '/api/onboarding/registrations/import-template', method: 'get' })
  }

  upload(url, organizationId, file) {
    const data = new FormData()
    data.append('organizationId', organizationId)
    data.append('file', file)
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url, method: 'post', data })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations/{id}/invitation — envoie (ou renvoie) le lien par e-mail ; l'ancien ne marche plus
  sendInvitation(id) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}/invitation`, method: 'post' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations/{id}/link — donne (ou redonne) le lien du tunnel ; l'ancien ne marche plus
  issueLink(id) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}/link`, method: 'post' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations/{id}/provision — lance ou reprend la mise en service
  provision(id) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}/provision`, method: 'post' })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // ── Tunnel public : c'est le jeton du lien qui ouvre, pas une session ──────

  // GET /api/onboarding/tunnel
  openTunnel(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel', method: 'get' })
  }

  // POST /api/onboarding/tunnel/applicant — l'étape « Vous »
  submitApplicant(token, data) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/applicant', method: 'post', data })
  }

  // POST /api/onboarding/tunnel/mandate/preview — le mandat à signer, pour l'IBAN saisi (déjà chez Stripe)
  previewMandate(token, stripePaymentMethodId) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/mandate/preview', method: 'post', data: { stripePaymentMethodId } })
  }

  // POST /api/onboarding/tunnel/mandate/signature — signer ce qui a été montré ; rend le dossier à jour
  signMandate(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/mandate/signature', method: 'post' })
  }

  // POST /api/onboarding/tunnel/card/setup — la demande d'enregistrement que Stripe.js confirme
  startCardSetup(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/card/setup', method: 'post' })
  }

  // POST /api/onboarding/tunnel/card/confirmation — l'issue relue chez Stripe ; rend le dossier à jour
  confirmCard(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/card/confirmation', method: 'post' })
  }

  // POST /api/onboarding/tunnel/deposit/payment — « Payer l'acompte » ; rend le dossier, et
  // depositClientSecret quand la banque demande une authentification
  payDeposit(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/deposit/payment', method: 'post' })
  }

  // POST /api/onboarding/tunnel/deposit/authorization — devant une approbation : l'acompte autorisé, prélevé à l'acceptation
  authorizeDeposit(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/deposit/authorization', method: 'post' })
  }

  // POST /api/onboarding/tunnel/deposit/confirmation — l'issue relue chez Stripe après l'authentification
  confirmDeposit(token) {
    return this.publicFetch(token, { url: '/api/onboarding/tunnel/deposit/confirmation', method: 'post' })
  }

  // GET /api/onboarding/tunnel/join/{linkToken} — la page d'un lien d'invitation
  joinPage(linkToken) {
    return this.anonymousFetch({ url: `/api/onboarding/tunnel/join/${encodeURIComponent(linkToken)}`, method: 'get' })
  }

  // POST /api/onboarding/tunnel/join/{linkToken} — envoyer de quoi entrer à cette adresse
  requestJoin(linkToken, email, language) {
    return this.anonymousFetch({ url: `/api/onboarding/tunnel/join/${encodeURIComponent(linkToken)}`, method: 'post', data: { email, language } })
  }

  // POST /api/onboarding/tunnel/join-confirmation — le ticket de l'e-mail contre un jeton de tunnel
  redeemJoinTicket(ticket, language) {
    return this.anonymousFetch({ url: '/api/onboarding/tunnel/join-confirmation', method: 'post', data: { ticket, language } })
  }

  // POST /api/onboarding/registrations/{id}/approval — accepter, ou refuser avec un motif
  decideApproval(id, approved, reason = null) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}/approval`, method: 'post', data: { approved, reason } })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  // POST /api/onboarding/registrations/{id}/cancellation — annuler une invitation qui n'a pas abouti (motif facultatif)
  cancel(id, reason = null) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ url: `/api/onboarding/registrations/${id}/cancellation`, method: 'post', data: { reason } })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  anonymousFetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ ...options, _noToken: true })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }

  publicFetch(token, options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({ ...options, headers: { Authorization: `Bearer ${token}` }, _noToken: true })
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new RegistrationRepository()
